import { faRectangle } from '@fortawesome/pro-regular-svg-icons'
import {
  faAlignCenter,
  faAlignLeft,
  faAlignRight,
  faBold,
  faBracketsCurly,
  faChevronDown,
  faCode,
  faDistributeSpacingVertical,
  faEllipsisH,
  faEraser,
  faFontCase,
  faHorizontalRule,
  faImage,
  faItalic,
  faLink,
  faListOl,
  faListUl,
  faPaperclip,
  faPlus,
  faQuoteRight,
  faStrikethrough,
  faUnderline,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Level } from '@tiptap/extension-heading'
import { Editor } from '@tiptap/react'
import React, { useRef } from 'react'
import { Popover } from '../../Popover/Popover'
import { usePopper } from '../../Popover/hooks/usePopper'
import { ButtonAttributes } from '../interfaces/ButtonAttributes'
import { ImageExtendedAttributes } from '../interfaces/ImageExtendedAttributes'
import { hasExtension } from '../utils/generateExtensions'
import { EditorButtonBubbleMenu } from './EditorButtonBubbleMenu'
import { EditorCommandButton } from './EditorCommandButton'
import { EditorCommandSpacer } from './EditorCommandSpacer'
import { EditorImageBubbleMenu } from './EditorImageBubbleMenu'
import { EditorLinkBubbleMenu, ILinkOptions } from './EditorLinkBubbleMenu'
import { EditorSpacerBubbleMenu } from './EditorSpacerBubbleMenu'

const transformFileToBase64 = async (files: File[]): Promise<ImageExtendedAttributes[]> => {
  return await Promise.all(
    files.map(
      (file) =>
        new Promise<ImageExtendedAttributes>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => {
            if (!reader.result || reader.result === null) {
              reject(new Error('No result'))
              return
            }
            resolve({
              src: reader.result?.toString(),
              alt: file.name,
              title: file.name,
            })
          }
          reader.onerror = reject
          reader.readAsDataURL(file)
        }),
    ),
  )
}

interface IProps {
  editor: Editor | null
  disabled?: boolean
  buttonOptions?: Partial<ButtonAttributes>
  linkOptions?: ILinkOptions
  hasAttachments?: boolean
  hasEmailExtension?: boolean
  onSelectFiles?: (file: File[]) => Promise<ImageExtendedAttributes[]>
}

export const EditorMenuBar = ({
  editor,
  disabled,
  buttonOptions,
  linkOptions,
  hasAttachments = false,
  hasEmailExtension = false,
  onSelectFiles: handleSelect = transformFileToBase64,
}: IProps) => {
  if (!editor) {
    return null
  }
  const levels: Level[] =
    editor?.extensionManager.extensions.find((e) => e.name === 'heading')?.options.levels || []
  const hasHeaderExtensions: boolean = levels.length > 0
  const hasBoldExtension: boolean = hasExtension(editor, 'bold')
  const hasItalicExtension: boolean = hasExtension(editor, 'italic')
  const hasStyleExtensions: boolean =
    hasExtension(editor, 'underline') ||
    hasExtension(editor, 'strikethrough') ||
    hasExtension(editor, 'code')
  const hasTextAlignExtension: boolean = hasExtension(editor, 'textAlign')
  const hasBulletListExtension: boolean = hasExtension(editor, 'bulletList')
  const hasOrderedListExtension: boolean = hasExtension(editor, 'orderedList')
  const hasLinkExtension: boolean = hasExtension(editor, 'link')
  const hasCodeExtension: boolean = hasExtension(editor, 'codeBlock')
  const hasImageExtension: boolean = hasExtension(editor, 'image')
  const hasBlockQuoteExtension: boolean = hasExtension(editor, 'blockquote')
  const hasHorizonatalRuleExtension: boolean = hasExtension(editor, 'horizontalRule')
  const hasButtonExtension: boolean = hasExtension(editor, 'button')
  const hasVariableExtension: boolean = hasExtension(editor, 'variable')
  const hasTextStyleExtensions: boolean =
    hasHeaderExtensions || hasBoldExtension || hasItalicExtension || hasStyleExtensions
  const hasFormattingExtensions: boolean =
    hasTextAlignExtension || hasBulletListExtension || hasOrderedListExtension
  const hasInsertableExtensions: boolean =
    hasLinkExtension ||
    hasCodeExtension ||
    hasImageExtension ||
    hasBlockQuoteExtension ||
    hasHorizonatalRuleExtension ||
    hasButtonExtension ||
    hasVariableExtension
  if (!hasTextStyleExtensions && !hasFormattingExtensions && !hasInsertableExtensions) return null
  return (
    <div className="mb-3 position-sticky top-0 bg-body z-1">
      <EditorImageBubbleMenu editor={editor} />
      <EditorSpacerBubbleMenu editor={editor} />
      <EditorButtonBubbleMenu editor={editor} linkOptions={linkOptions} />
      <EditorLinkBubbleMenu editor={editor} linkOptions={linkOptions} />
      {hasHeaderExtensions && <TextStyleButton editor={editor} disabled={disabled} />}
      {hasBoldExtension && (
        <EditorCommandButton
          title="Bold"
          disabled={disabled}
          isActive={() => editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <FontAwesomeIcon icon={faBold} fixedWidth={true} />
        </EditorCommandButton>
      )}
      {hasItalicExtension && (
        <EditorCommandButton
          title="Italic"
          disabled={disabled}
          isActive={() => editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <FontAwesomeIcon icon={faItalic} fixedWidth={true} />
        </EditorCommandButton>
      )}
      {hasStyleExtensions && <MoreFormattingButton editor={editor} disabled={disabled} />}
      {hasTextStyleExtensions && hasFormattingExtensions && <EditorCommandSpacer />}
      {hasTextAlignExtension && <TextAlignButton editor={editor} disabled={disabled} />}
      {hasBulletListExtension && (
        <EditorCommandButton
          title="Bullet list"
          disabled={disabled}
          isActive={() => editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <FontAwesomeIcon icon={faListUl} fixedWidth={true} />
        </EditorCommandButton>
      )}
      {hasOrderedListExtension && (
        <EditorCommandButton
          title="Numbered list"
          disabled={disabled}
          isActive={() => editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <FontAwesomeIcon icon={faListOl} fixedWidth={true} />
        </EditorCommandButton>
      )}
      {hasFormattingExtensions && hasInsertableExtensions && <EditorCommandSpacer />}
      {hasLinkExtension && (
        <EditorCommandButton
          title="Link"
          disabled={disabled || editor.isActive('codeBlock')}
          isActive={() => editor.isActive('link')}
          onClick={() => {
            if (editor.isActive('link')) {
              return editor.chain().unsetLink().focus().run()
            }
            const hasSelectedText = !editor.state.selection.empty
            if (hasSelectedText) {
              return editor.chain().setLink({ href: '' }).focus().run()
            }
            let cursorOffset = editor.state.selection.anchor
            editor.chain().selectParentNode().focus().run()

            const nodeStartIndex = editor.state.selection.from + 1
            const nodeEndIndex = editor.state.selection.to
            cursorOffset -= nodeStartIndex
            const nodeText = editor.state.selection.$from.parent.textBetween(
              0,
              nodeEndIndex - nodeStartIndex,
              ' ',
              '\n',
            )

            const linkStartOffset = nodeText.substring(0, cursorOffset).search(/\s\S*$/)
            const startText = nodeText.substring(linkStartOffset + 1)
            let wordLength = startText.search(/\s/)
            wordLength = wordLength < 0 ? startText.length : wordLength
            return editor
              .chain()
              .setTextSelection({
                from: nodeStartIndex + linkStartOffset + 1,
                to: nodeStartIndex + linkStartOffset + wordLength + 1,
              })
              .setLink({ href: '' })
              .focus()
              .run()
          }}
        >
          <FontAwesomeIcon icon={faLink} fixedWidth={true} />
        </EditorCommandButton>
      )}
      {hasInsertableExtensions && (
        <InsertSection
          editor={editor}
          disabled={disabled}
          buttonOptions={buttonOptions}
          hasEmailExtension={hasEmailExtension}
          hasAttachments={hasAttachments}
          onUpload={handleSelect}
        />
      )}
    </div>
  )
}

interface ITextStyleButtonProps
  extends React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {
  editor: Editor
}

export const TextStyleButton = ({
  editor,
  className,
  style,
  onClick: handleClick,
  ...props
}: ITextStyleButtonProps) => {
  const { target, toggle, targetProps, ...tooltipProps } = usePopper<HTMLButtonElement>({
    placement: 'bottom-start',
  })

  const levels: Level[] =
    editor?.extensionManager.extensions.find((e) => e.name === 'heading')?.options.levels || []
  const hasHeaders: boolean = levels.length > 0
  if (!hasHeaders) return null

  return (
    <>
      <EditorCommandButton
        ref={target as any}
        title="Text style"
        {...props}
        {...targetProps}
        onClick={(e) => {
          toggle()
          handleClick?.(e)
        }}
      >
        <span className="d-none d-lg-inline text-capitalize">
          <TextStyleLabel editor={editor} />
        </span>
        <FontAwesomeIcon icon={faFontCase} fixedWidth className="d-inline d-lg-none" />
        <FontAwesomeIcon icon={faChevronDown} fixedWidth transform="shrink-6" />
      </EditorCommandButton>
      <Popover {...tooltipProps} toggle={toggle}>
        <EditorCommandButton
          className="dropdown-item"
          activeClassName="active"
          isActive={() => editor.isActive('paragraph')}
          onClick={() => editor.chain().focus().setParagraph().run()}
        >
          <span>Normal text</span>
        </EditorCommandButton>
        {levels.sort().map((level) => (
          <EditorCommandButton
            key={`heading-button-${level}`}
            className="dropdown-item"
            activeClassName="active"
            isActive={() => editor.isActive('heading', { level })}
            onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
          >
            <span className={`h${level}`} style={{ lineHeight: '0.8' }}>
              <span style={{ fontSize: '50%' }}>Heading {level}</span>
            </span>
          </EditorCommandButton>
        ))}
      </Popover>
      <span className="px-3 pb-0">
        <span className="btn-separator"></span>
      </span>
    </>
  )
}

const TextStyleLabel = ({ editor }: { editor: Editor }) => {
  if (editor.isActive('heading', { level: 1 })) {
    return <>Heading 1</>
  } else if (editor.isActive('heading', { level: 2 })) {
    return <>Heading 2</>
  } else if (editor.isActive('heading', { level: 3 })) {
    return <>Heading 3</>
  } else if (editor.isActive('heading', { level: 4 })) {
    return <>Heading 4</>
  } else if (editor.isActive('heading', { level: 5 })) {
    return <>Heading 5</>
  } else if (editor.isActive('heading', { level: 6 })) {
    return <>Heading 6</>
  }
  return <>Normal text</>
}

interface IMoreFormattingButtonProps
  extends React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {
  editor: Editor
}

const MoreFormattingButton = ({
  editor,
  className,
  style,
  onClick: handleClick,
  ...props
}: IMoreFormattingButtonProps) => {
  const { target, toggle, targetProps, ...tooltipProps } = usePopper<HTMLButtonElement>({
    placement: 'bottom',
  })
  const isActive: boolean = [
    editor.isActive('underline'),
    editor.isActive('strike'),
    editor.isActive('code'),
  ].some((i) => i)
  return (
    <>
      <EditorCommandButton
        ref={target as any}
        title="More formatting"
        type="button"
        {...props}
        {...targetProps}
        isActive={() => isActive}
        onClick={(e) => {
          toggle()
          handleClick?.(e)
        }}
      >
        <FontAwesomeIcon icon={faEllipsisH} fixedWidth />
      </EditorCommandButton>
      <Popover {...tooltipProps} toggle={toggle}>
        <EditorCommandButton
          className="dropdown-item"
          activeClassName="active"
          isActive={() => editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <FontAwesomeIcon icon={faUnderline} fixedWidth={true} className="me-2" />
          Underline
        </EditorCommandButton>
        <EditorCommandButton
          className="dropdown-item"
          activeClassName="active"
          isActive={() => editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <FontAwesomeIcon icon={faStrikethrough} fixedWidth={true} className="me-2" />
          Strikethrough
        </EditorCommandButton>
        <EditorCommandButton
          className="dropdown-item"
          activeClassName="active"
          isActive={() => editor.isActive('code')}
          onClick={() => editor.chain().focus().toggleCode().run()}
        >
          <FontAwesomeIcon icon={faCode} fixedWidth={true} className="me-2" />
          Code
        </EditorCommandButton>
        <EditorCommandButton
          className="dropdown-item"
          activeClassName="active"
          onClick={() => {
            editor.chain().focus().unsetAllMarks().run()
            editor.chain().focus().clearNodes().run()
          }}
        >
          <FontAwesomeIcon icon={faEraser} fixedWidth={true} className="me-2" />
          Clear formatting
        </EditorCommandButton>
      </Popover>
    </>
  )
}

interface ITextAlignButtonProps
  extends React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {
  editor: Editor
}

const TextAlignButton = ({ editor, className, style, ...props }: ITextAlignButtonProps) => {
  const { target, toggle, targetProps, ...popoverProps } = usePopper<HTMLButtonElement>({
    placement: 'bottom',
  })

  const hasAlignExtension: boolean = !!editor?.extensionManager.extensions.find(
    (e) => e.name === 'textAlign',
  )
  if (!hasAlignExtension) {
    return null
  }
  const isActive: boolean = [
    editor.isActive({ textAlign: 'left' }),
    editor.isActive({ textAlign: 'center' }),
    editor.isActive({ textAlign: 'right' }),
  ].some((i) => i)

  return (
    <>
      <EditorCommandButton
        ref={target as any}
        title="Text align"
        {...props}
        {...targetProps}
        isActive={() => isActive && popoverProps.isOpen}
      >
        {(editor.isActive({ textAlign: 'left' }) || !isActive) && (
          <FontAwesomeIcon icon={faAlignLeft} />
        )}
        {editor.isActive({ textAlign: 'center' }) && <FontAwesomeIcon icon={faAlignCenter} />}
        {editor.isActive({ textAlign: 'right' }) && <FontAwesomeIcon icon={faAlignRight} />}
        <FontAwesomeIcon icon={faChevronDown} fixedWidth transform="shrink-6" />
      </EditorCommandButton>
      <Popover {...popoverProps} toggle={toggle}>
        <div className="d-flex">
          <EditorCommandButton
            title="Align left"
            className="dropdown-item px-3"
            activeClassName="active"
            style={{ borderRadius: '0.3rem' }}
            isActive={() => editor.isActive({ textAlign: 'left' })}
            onClick={() => {
              if (editor.isActive({ textAlign: 'left' })) {
                editor.chain().focus().setTextAlign('left').run()
              } else {
                editor.chain().focus().setTextAlign('left').run()
              }
            }}
          >
            <FontAwesomeIcon icon={faAlignLeft} fixedWidth />
          </EditorCommandButton>
          <EditorCommandButton
            title="Align center"
            className="dropdown-item px-3"
            activeClassName="active"
            style={{ borderRadius: '0.3rem' }}
            isActive={() => editor.isActive({ textAlign: 'center' })}
            onClick={() => {
              if (editor.isActive({ textAlign: 'center' })) {
                editor.chain().focus().setTextAlign('left').run()
              } else {
                editor.chain().focus().setTextAlign('center').run()
              }
            }}
          >
            <FontAwesomeIcon icon={faAlignCenter} fixedWidth />
          </EditorCommandButton>
          <EditorCommandButton
            title="Align right"
            className="dropdown-item px-3"
            activeClassName="active"
            style={{ borderRadius: '0.3rem' }}
            isActive={() => editor.isActive({ textAlign: 'right' })}
            onClick={() => {
              if (editor.isActive({ textAlign: 'right' })) {
                editor.chain().focus().setTextAlign('left').run()
              } else {
                editor.chain().focus().setTextAlign('right').run()
              }
            }}
          >
            <FontAwesomeIcon icon={faAlignRight} fixedWidth />
          </EditorCommandButton>
        </div>
      </Popover>
    </>
  )
}

interface InsertSectionProps {
  editor: Editor
  disabled?: boolean
  buttonOptions?: Partial<ButtonAttributes>
  hasEmailExtension?: boolean
  hasAttachments?: boolean
  onUpload?: (file: File[]) => Promise<ImageExtendedAttributes[]>
}

const InsertSection = ({
  editor,
  disabled,
  buttonOptions,
  hasEmailExtension = false,
  hasAttachments = false,
  onUpload: handleUpload = transformFileToBase64,
}: InsertSectionProps) => {
  const fileRef = useRef<HTMLInputElement>(null)
  const { target, toggle, targetProps, ...tooltipProps } = usePopper<HTMLButtonElement>({
    placement: 'bottom',
  })
  const hasCodeExtension: boolean = hasExtension(editor, 'codeBlock')
  const hasImageExtension: boolean = hasExtension(editor, 'image')
  const hasBlockQuoteExtension: boolean = hasExtension(editor, 'blockquote')
  const hasHorizonatalRuleExtension: boolean = hasExtension(editor, 'horizontalRule')
  const hasSpacerExtension: boolean = hasExtension(editor, 'spacer')
  const hasButtonExtension: boolean = hasExtension(editor, 'button')
  const hasVariableExtension: boolean = hasExtension(editor, 'variable')
  const isActive: boolean = [
    editor.isActive('code'),
    editor.isActive('image'),
    editor.isActive('blockquote'),
    editor.isActive('horizontalRule'),
    editor.isActive('spacer'),
    editor.isActive('button'),
    editor.isActive('variable'),
  ].some((i) => i)
  return (
    <>
      <EditorCommandButton
        title="Insert"
        ref={target}
        {...targetProps}
        className="btn d-inline-block d-lg-none"
        isActive={() => isActive}
        onClick={() => toggle()}
      >
        <FontAwesomeIcon icon={faPlus} fixedWidth />
        <FontAwesomeIcon icon={faChevronDown} fixedWidth transform="shrink-6" />
      </EditorCommandButton>
      <Popover {...tooltipProps} toggle={toggle}>
        {hasCodeExtension && (
          <EditorCommandButton
            disabled={disabled}
            className="dropdown-item"
            activeClassName="active"
            isActive={() => editor.isActive('codeBlock')}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          >
            <FontAwesomeIcon icon={faCode} fixedWidth={true} className="me-2" /> Code snippet
          </EditorCommandButton>
        )}
        {hasImageExtension && (
          <EditorCommandButton
            disabled={disabled}
            className="dropdown-item"
            activeClassName="active"
            isActive={() => editor.isActive('image')}
            onClick={() => fileRef.current?.click()}
          >
            <FontAwesomeIcon icon={faImage} fixedWidth={true} className="me-2" /> Image
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChangeCapture={async (evt: React.ChangeEvent<HTMLInputElement>) => {
                if (!evt.target.files) return
                const files = await handleUpload(Array.from(evt.target.files))
                editor
                  .chain()
                  .focus()
                  .forEach(files, (file, { commands }) => commands.setImage(file))
                  .run()
              }}
            />
          </EditorCommandButton>
        )}
        {hasBlockQuoteExtension && (
          <EditorCommandButton
            disabled={disabled}
            className="dropdown-item"
            activeClassName="active"
            isActive={() => editor.isActive('blockquote')}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            <FontAwesomeIcon icon={faQuoteRight} fixedWidth={true} className="me-2" /> Quote
          </EditorCommandButton>
        )}
        {hasHorizonatalRuleExtension && (
          <EditorCommandButton
            disabled={disabled}
            className="dropdown-item"
            activeClassName="active"
            isActive={() => editor.isActive('horizontalRule')}
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
          >
            <FontAwesomeIcon icon={faHorizontalRule} fixedWidth={true} className="me-2" /> Divider
          </EditorCommandButton>
        )}
        {hasSpacerExtension && (
          <EditorCommandButton
            disabled={disabled}
            className="dropdown-item"
            activeClassName="active"
            isActive={() => editor.isActive('spacer')}
            onClick={() => editor.chain().focus().setSpacer().run()}
          >
            <FontAwesomeIcon
              icon={faDistributeSpacingVertical}
              fixedWidth={true}
              className="me-2"
            />{' '}
            Spacer
          </EditorCommandButton>
        )}
        {hasButtonExtension && (
          <EditorCommandButton
            disabled={disabled}
            className="dropdown-item"
            activeClassName="active"
            isActive={() => editor.isActive('button')}
            onClick={() => editor.chain().focus().setButton().run()}
          >
            <FontAwesomeIcon icon={faPlus} fixedWidth={true} className="me-2" /> Button
          </EditorCommandButton>
        )}
        {hasVariableExtension && (
          <EditorCommandButton
            disabled={disabled}
            className="dropdown-item"
            activeClassName="active"
            isActive={() => editor.isActive('variable')}
            onClick={() => editor.chain().insertContent(' {{').focus().run()}
          >
            <FontAwesomeIcon icon={faBracketsCurly} fixedWidth={true} className="me-2" /> Variable
          </EditorCommandButton>
        )}
      </Popover>
      <span className="d-none d-lg-inline-block">
        {hasCodeExtension && (
          <EditorCommandButton
            title="Code snippet"
            disabled={disabled}
            isActive={() => editor.isActive('codeBlock')}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          >
            <FontAwesomeIcon icon={faCode} fixedWidth={true} />
          </EditorCommandButton>
        )}
        {hasImageExtension && (
          <EditorCommandButton
            title="Image"
            disabled={disabled}
            isActive={() => editor.isActive('image')}
            onClick={() => fileRef.current?.click()}
          >
            <FontAwesomeIcon icon={faImage} fixedWidth={true} />
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChangeCapture={async (evt: React.ChangeEvent<HTMLInputElement>) => {
                if (!evt.target.files) return
                const files = await handleUpload(Array.from(evt.target.files))
                editor
                  .chain()
                  .focus()
                  .forEach(files, (file, { commands }) => commands.setImage(file))
                  .run()
              }}
            />
          </EditorCommandButton>
        )}
        {hasBlockQuoteExtension && (
          <EditorCommandButton
            title="Quote"
            disabled={disabled}
            isActive={() => editor.isActive('blockquote')}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            <FontAwesomeIcon icon={faQuoteRight} fixedWidth={true} />
          </EditorCommandButton>
        )}
        {hasHorizonatalRuleExtension && (
          <EditorCommandButton
            title="Divider"
            disabled={disabled}
            isActive={() => editor.isActive('horizontalRule')}
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
          >
            <FontAwesomeIcon icon={faHorizontalRule} fixedWidth={true} />
          </EditorCommandButton>
        )}
        {hasSpacerExtension && (
          <EditorCommandButton
            title="Spacer"
            disabled={disabled}
            isActive={() => editor.isActive('spacer')}
            onClick={() => editor.chain().focus().setSpacer().run()}
          >
            <FontAwesomeIcon icon={faDistributeSpacingVertical} fixedWidth={true} />
          </EditorCommandButton>
        )}
        {hasButtonExtension && (
          <EditorCommandButton
            title="Button"
            disabled={disabled}
            isActive={() => editor.isActive('button')}
            onClick={() => editor.chain().focus().setButton(buttonOptions).run()}
          >
            <span className="fa-layers fa-fw">
              <FontAwesomeIcon icon={faRectangle} fixedWidth={true} />
              <FontAwesomeIcon icon={faHorizontalRule} fixedWidth={true} transform="shrink-8" />
            </span>
          </EditorCommandButton>
        )}
        {hasVariableExtension && (
          <EditorCommandButton
            title="Variable"
            disabled={disabled}
            isActive={() => editor.isActive('variable')}
            onClick={() => editor.chain().insertContent(' {{').focus().run()}
          >
            <FontAwesomeIcon icon={faBracketsCurly} fixedWidth={true} />
          </EditorCommandButton>
        )}
        {hasEmailExtension && (
          <EditorCommandButton
            title="Attachment"
            disabled={disabled}
            isActive={() => hasAttachments}
            onClick={() => fileRef.current?.click()}
          >
            <FontAwesomeIcon icon={faPaperclip} fixedWidth={true} />
            <input
              ref={fileRef}
              type="file"
              style={{ display: 'none' }}
              onChangeCapture={async (evt: React.ChangeEvent<HTMLInputElement>) => {
                if (!evt.target.files) return
                const files = await handleUpload?.(Array.from(evt.target.files))
                editor
                  .chain()
                  .focus()
                  .forEach(files, (file, { commands }) => commands.setImage(file))
                  .run()
              }}
              multiple
            />
          </EditorCommandButton>
        )}
      </span>
    </>
  )
}
